import { useEffect, useRef, useState } from 'react';

const storageKey = 'arcnova-cookie-preferences';
const categories = [
  {
    key: 'marketing',
    name: 'Marketing',
    description:
      'Cookies used to deliver advertising that is more relevant to you and your interests.',
  },
  {
    key: 'personalization',
    name: 'Personalization',
    description:
      'Cookies allowing the website to remember choices you make (such as your user name, language, or the region you are in).',
  },
  {
    key: 'analytics',
    name: 'Analytics',
    description:
      'Cookies helping understand how this website performs, how visitors interact with the site, and whether there may be technical issues.',
  },
] as const;
type Preferences = Record<(typeof categories)[number]['key'], boolean>;
const empty: Preferences = { marketing: false, personalization: false, analytics: false };

export function CookiePreferences() {
  const [banner, showBanner] = useState(false);
  const [preferences, setPreferences] = useState<Preferences>(empty);
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (!stored) showBanner(true);
      else {
        const parsed: unknown = JSON.parse(stored);
        if (typeof parsed === 'object' && parsed !== null)
          setPreferences(
            Object.fromEntries(
              categories.map(({ key }) => [key, (parsed as Preferences)[key] === true]),
            ) as Preferences,
          );
      }
    } catch {
      showBanner(true);
    }
  }, []);
  function save(values: Preferences) {
    setPreferences(values);
    try {
      localStorage.setItem(storageKey, JSON.stringify(values));
    } catch {
      /* Consent remains in memory when storage is unavailable. */
    }
    showBanner(false);
    dialog.current?.close();
  }
  const allow = () => save({ marketing: true, personalization: true, analytics: true });
  const description = (
    <div className="fs-cc_description">
      By clicking &quot;Accept all cookies&quot;, you agree to storing cookies on your device to
      enhance site navigation, analyze site usage and assist in our marketing efforts as outlined in
      our{' '}
      <a href="/privacy-policy" target="_blank" rel="noreferrer" className="fs-cc_link">
        Privacy Policy
      </a>{' '}
      and{' '}
      <a href="/cookies-policy" target="_blank" rel="noreferrer" className="fs-cc_link">
        Cookies Policy
      </a>
    </div>
  );
  return (
    <div className="fs-consent_component">
      {banner && (
        <section className="fs-cc_cookie-component" aria-label="Cookie settings">
          <div className="fs-cc_modal">
            <button
              type="button"
              aria-label="Close Cookie Popup"
              className="fs-cc_close-button w-inline-block"
              onClick={() => save(empty)}
            >
              <span className="fs-cc_close-button-line" />
              <span className="fs-cc_close-button-line is-2nd" />
            </button>
            <div className="fs_cc-modal-content">
              <div className="fs-cc_title">Cookie settings</div>
              {description}
            </div>
            <div className="fs-cc_modal-buttons">
              <button type="button" className="fs-cc_button w-button" onClick={allow}>
                accept all cookies
              </button>
              <button
                type="button"
                className="fs-cc_button is-secondary w-button"
                onClick={() => dialog.current?.showModal()}
              >
                cookie settings
              </button>
            </div>
          </div>
        </section>
      )}
      <dialog ref={dialog} className="cookie-preference-dialog" aria-labelledby="cookie-title">
        <div className="cookie-preference_wrapper">
          <div className="fs-cc_modal">
            <button
              type="button"
              aria-label="Close Cookie Preference Manager"
              className="fs-cc_close-button w-inline-block"
              onClick={() => dialog.current?.close()}
            >
              <span className="fs-cc_close-button-line" />
              <span className="fs-cc_close-button-line is-2nd" />
            </button>
            <div className="fs_cc-modal-content">
              <div id="cookie-title" className="fs-cc_title">
                Cookie settings
              </div>
              {description}
              <div className="fs-cc_form">
                <div className="fs-cc_preferences">
                  <div className="fs-cc_checkbox is--not-allowed w-clearfix">
                    <div className="fs-cc_checkbox-button is-required" />
                    <div className="fs-cc_checkbox-label is--not-allowed">
                      Strictly necessary (always active)
                    </div>
                    <div className="fs-cc_checkbox-description is--not-allowed">
                      Cookies required to enable basic website functionality.
                    </div>
                  </div>
                  {categories.map((category) => (
                    <label className="w-checkbox fs-cc_checkbox w-clearfix" key={category.key}>
                      <input
                        type="checkbox"
                        className="fs-cc_checkbox-button"
                        checked={preferences[category.key]}
                        onChange={(event) =>
                          setPreferences({ ...preferences, [category.key]: event.target.checked })
                        }
                      />
                      <span className="fs-cc_checkbox-label w-form-label">{category.name}</span>
                      <span className="fs-cc_checkbox-description">{category.description}</span>
                    </label>
                  ))}
                </div>
                <div className="fs-cc_preference-buttons">
                  <button type="button" className="fs-cc_button w-button" onClick={allow}>
                    accept all cookies
                  </button>
                  <button
                    type="button"
                    className="fs-cc_button is-secondary w-button"
                    onClick={() => save(preferences)}
                  >
                    save settings
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </dialog>
      {!banner && (
        <button type="button" className="cookie-reopen" onClick={() => dialog.current?.showModal()}>
          Cookie settings
        </button>
      )}
    </div>
  );
}
